<template>
  <div :class="{ expanded: video.expanded }" class="video-item">
    <div class="flex items-start md:items-center py-2 text-tiny md:text-sm">
      <div class="w-4/12">
        <div class="video-name">
          <div v-if="video.exportStatus === 'EXPORTED'" class="title">
            <div class="group/title flex items-center gap-1 min-w-0">
              <Title ref="titleRef" class="h-8 p-1 md:p-2 md:-ml-2 block hover:bg-surface-3 text-tiny md:text-sm min-w-0 max-w-full" :editable="true"
                :title="archiveName" @changed="onNameChange" @edit="" />
              <FormButton @click="$refs.titleRef.editTitle()" title="Rename"
                custom-classes="shrink-0 opacity-0 group-hover/title:opacity-100 transition-opacity text-surface-7 hover:text-white">
                <icon-base width-view-box="16" height-view-box="16">
                  <icon-edit />
                </icon-base>
              </FormButton>
            </div>
            <span class="text-tiny md:text-sm font-medium text-surface-7 ml-1 md:ml-0 md:mr-2 uppercase">{{
              video.size
              }}</span>
            <span v-if="video.abr">
              <Badge type="orange" class="inline-flex">ABR</Badge>
            </span>
          </div>
          <span class=" text-dark-9" v-if="video.exportStatus !== 'EXPORTED'">{{ archiveName }}</span>
          <span class="ml-2" v-if="video.exportStatus !== 'EXPORTED' && video.abr">
            <Badge type="orange" class="inline-flex">ABR</Badge>
          </span>
          <span class="ml-2" v-if="video.exportStatus !== 'EXPORTED' && (isLive || (video.blocking && isStreamLive)) && streamDvrEnabled">
            <Badge type="online" class="inline-flex">LIVE</Badge>
          </span>
        </div>
      </div>
      <div class="w-3/12 md:w-2/12 text-surface-9">
        <span class="text-dark-9"
          v-if="video.exportStatus === 'EXPORTED' || video.exportStatus === 'EXPORTING' || (video.duration && !isLive)">
          {{ humanTime(video.duration) || "Unknown" }}
        </span>
      </div>
      <div class="w-3/12 md:w-2/12 min-w-0 text-tiny leading-tight">
        <div class="text-surface-9">{{ dateOnly(video.startTimeDate) }}</div>
        <div class="text-surface-7 mt-0.5">{{ timeOnly(video.startTimeDate) }}</div>
      </div>
      <div class="self-center w-2/12 md:w-4/12 min-w-0">
        <div class="text-right pr-0 recording-action flex flex-wrap gap-2 items-center justify-end">
          <span v-if="video.exportStatus == 'EXPORTING'" class="inline-flex items-center gap-2 text-surface-8">
            <span class="hidden md:inline">Exporting</span>
            <span class="inline-block size-3.5 rounded-full border-2 border-surface-6 border-t-transparent animate-spin"></span>
          </span>
          <span v-else-if="video.exportProcessing" class="inline-flex items-center gap-2 text-surface-8">
            <span class="hidden md:inline">Starting…</span>
            <span class="inline-block size-3.5 rounded-full border-2 border-surface-6 border-t-transparent animate-spin"></span>
          </span>
          <FormButton class="hidden md:inline-flex" v-if="video.exportStatus === 'EXPORTED'" type="secondary"
            size="sm" @click="toggleMetadata(video)" :disabled="expiredSub">
            Embed Link
          </FormButton>
          <Tooltip v-if="video.exportStatus === 'NOT_EXPORTED' && vodStorageExceeded"
            :content="vodStorageExceededTooltip" class="inline-block" tabindex="0">
            <drop-down-menu size="sm" type="secondary" class="!hidden md:!block" position="right" :disabled="true">
              <template #toggle-button>
                Save as VOD
                <span class="sr-only">Save as VOD</span>
              </template>
            </drop-down-menu>
          </Tooltip>
          <drop-down-menu v-else-if="video.exportStatus === 'NOT_EXPORTED' && !video.exportProcessing" size="sm" type="secondary"
            list-classes="!border !border-surface-4 !rounded-md" wrapper-class="mt-0.5" class="!hidden md:!block"
            position="right" :disabled="video.exportProcessing">
            <template #toggle-button>
              Save as VOD
              <span class="sr-only">Save as VOD</span>
            </template>
            <template #menu-items>
              <drop-down-menu-item @click="onVodSaveWhole">
                <icon-base class="mr-2">
                  <icon-download />
                </icon-base>
                Save whole video
              </drop-down-menu-item>
              <drop-down-menu-item @click="onVodSaveCustom('Audio track 1')">
                <icon-base class="mr-2">
                  <icon-select-range />
                </icon-base>
                Select a range
              </drop-down-menu-item>
              <drop-down-menu-item @click="onVodSaveWithCaptions">
                <icon-base class="mr-2">
                  <icon-captions />
                </icon-base>
                Save video with captions
              </drop-down-menu-item>
            </template>
          </drop-down-menu>
          <Tooltip v-if="video.exportStatus === 'FAILED'" content="Exporting process failed, try again">
            <icon-base class="text-danger" fill="none">
              <icon-info />
            </icon-base>
          </Tooltip>
          <FormButton type="secondary" size="sm" v-if="video.exportStatus === 'FAILED' && !isLive"
            @click="exportDvrArchive2">
            Try again
          </FormButton>
          <FormButton class="hidden md:inline-flex shrink-0" v-if="video.exportStatus === 'NOT_EXPORTED' && !video.exportProcessing" size="sm"
            isIcon type="secondary" @click="playPreview('Audio track 1', false, 'preview-video')">
            <icon-base>
              <icon-play />
            </icon-base>
          </FormButton>
          <FormButton class="hidden md:inline-flex" v-if="
            video.exportStatus === 'EXPORTED' && video.downloadUrl && allowOverdueVodDownload && (!expiredSub || video.exportStatus === 'NOT_EXPORTED')
          " size="sm" isIcon type="secondary" @click="downloadDvrArchive()">
            <icon-base>
              <icon-download />
            </icon-base>
          </FormButton>
          <span class="inline-flex gap-x-2 items-center" v-else-if="
            video.exportStatus !== 'EXPORTING' && video.downloadUrl && allowOverdueVodDownload && (!expiredSub || video.exportStatus === 'NOT_EXPORTED')
          ">
            <drop-down-menu size="sm"
              customClasses="button !border-none form-button inline-flex items-center !rounded-md leading-none text-nowrap box-border font-medium hover:no-underline disabled:bg-surface-5 disabled:ring-surface-5 disabled:text-surface-7 max-w-full truncate grow-0 p-0 justify-center border-none h-7 w-7 text-tiny text-surface-text-1 hover:text-white hover:text-white ring-1 ring-surface-4 hover:text-white hover:ring-surface-6 focus:ring-surface-6 hidden md:inline-flex"
              list-classes="!border !border-surface-4 !rounded-md overflow-y-visible" wrapper-class="mt-0.5"
              class="!hidden md:!block" position="right" :disabled="video.exportProcessing"
              @toggle="onDownloadMenuToggle">
              <template #toggle-button>
                <icon-base fill="none">
                  <icon-download />
                </icon-base>
                <span class="sr-only">Download</span>
              </template>
              <template #menu-items>
                <drop-down-menu-item  position="right"
                customClasses="h-8 p-2 text-surface-text-1 hover:bg-surface-3 hover:text-white text-nowrap leading-none text-tiny flex items-center w-full" @click="downloadDvrArchive()">
                  <icon-base class="mr-2" fill="none">
                    <icon-download />
                  </icon-base>
                  Download whole video
                </drop-down-menu-item>
                <drop-down-menu-item  position="right"
                customClasses="h-8 p-2 text-surface-text-1 hover:bg-surface-3 hover:text-white text-nowrap leading-none text-tiny flex items-center w-full" @click="onVodSaveCustom('Audio track 1', 'download-video')">
                  <icon-base class="mr-2" fill="none">
                    <icon-select-range />
                  </icon-base>
                  Select a range
                </drop-down-menu-item>
                <drop-down-menu-item isCustom>
                  <drop-down-menu v-if="audioTrackNames.length > 1" position="right"
                    customClasses="h-8 p-2 text-surface-text-1 hover:bg-surface-3 hover:text-white text-nowrap leading-none text-tiny flex items-center w-full">
                    <template #toggle-button>
                      <icon-base class="mr-2">
                        <icon-gear />
                      </icon-base>
                      Download Video with specific audio track
                      <icon-base class="ml-2">
                        <icon-chevron-right />
                      </icon-base>
                    </template>
                    <template #menu-items>
                      <drop-down-menu-item v-for="trackName in audioTrackNames" :key="trackName"
                        @click="onVodSaveCustom(trackName, 'download-video')">
                        Video with {{ trackName }}
                      </drop-down-menu-item>
                    </template>
                  </drop-down-menu>
                  <drop-down-menu position="right"
                    customClasses="h-8 p-2 text-surface-text-1 hover:bg-surface-3 hover:text-white text-nowrap leading-none text-tiny flex items-center w-full">
                    <template #toggle-button>
                      <icon-base class="mr-2">
                        <icon-gear />
                      </icon-base>
                      Download Audio track only
                      <icon-base class="ml-auto">
                        <icon-chevron-right />
                      </icon-base>
                    </template>
                    <template #menu-items>
                      <drop-down-menu-item v-if="audioTracksLoading" isText>
                        Loading tracks…
                      </drop-down-menu-item>
                      <template v-else>
                        <drop-down-menu-item v-for="trackName in audioTrackNames" :key="trackName"
                          @click="onVodAudioSaveCustom(trackName, 'download-audio')">
                          {{ trackName }}
                        </drop-down-menu-item>
                      </template>
                    </template>
                  </drop-down-menu>
                </drop-down-menu-item>
              </template>
            </drop-down-menu>
            <FormButton type="danger" size="sm" class="hidden md:inline-flex" isIcon
              @click="requestDeleteDvrArchive()">
              <icon-base fill="none">
                <icon-bin />
              </icon-base>
            </FormButton>
          </span>
          <Tooltip v-if="
            (video.exportStatus !== 'EXPORTING' && video.downloadUrl && !allowOverdueVodDownload) || (expiredSub && video.exportStatus === 'EXPORTED')
          " class="inline-block" tabindex="0" :hide="600">
            <FormButton type="danger" size="sm" class="" isIcon disabled>
              <icon-base fill="none">
                <icon-download />
              </icon-base>
            </FormButton>
            <template #content>
              <span v-html="disabledTooltipMessage" />
            </template>
          </Tooltip>
          <drop-down-menu size="sm" listWidth="w-72"
            custom-classes="inline-flex items-center justify-center p-0 h-7 w-7 !rounded-md text-tiny text-surface-text-1 hover:text-white ring-1 ring-surface-4 hover:ring-surface-6 focus:ring-surface-6"
            list-classes="!border !border-surface-4 !rounded-lg !p-1" wrapper-class="mt-0.5"
            :class="{ 'md:hidden': video.exportStatus != 'EXPORTED' }" class="inline-flex" position="right">
            <template #toggle-button>
              <icon-base fill="none">
                <icon-elipsis />
              </icon-base>
            </template>
            <template #menu-items>
              <drop-down-menu-item
                v-if="video.exportStatus === 'EXPORTED'" isCustom>
                <div
                  class="relative rounded-md transition-colors"
                  :class="{ 'hover:bg-surface-3': !isPreRecordedLimitReached && !disableCreatePreRecorderedStream }">
                  <FormButton
                    custom-classes="w-full grid grid-cols-[16px_1fr] gap-x-2.5 items-start text-left !p-3 md:!p-2 hover:bg-transparent disabled:opacity-40 disabled:hover:bg-transparent"
                    @click="onCreatePrerecord(video)"
                    :disabled="disableCreatePreRecorderedStream || isPreRecordedLimitReached">
                    <icon-base class="row-span-2 self-center text-surface-text-1" fill="none">
                      <icon-camera-cinema />
                    </icon-base>
                    <span class="text-base md:text-tiny font-medium text-surface-text-1 leading-snug pr-12">Create a pre-recorded stream</span>
                    <span class="text-tiny text-surface-7 leading-snug mt-0.5 font-normal whitespace-nowrap">Schedule time for this file to go live</span>
                  </FormButton>
                  <div class="absolute top-2 -translate-y-px right-2 z-10" @click.stop>
                    <Tooltip
                      wrapper-class="inline-flex"
                      :interactive="isPreRecordedLimitReached"
                      :html="isPreRecordedLimitReached"
                      :content="preRecordedUsageTooltip"
                      popper-class="max-w-[220px] text-sm"
                      :hide="600">
                      <Badge
                        size="sm"
                        pill
                        inline
                        :class="isPreRecordedLimitReached ? '!text-error !bg-error-transparent' : ''">
                        {{ scheduledStreams.length }}/{{ maxScheduledStreams }}
                      </Badge>
                    </Tooltip>
                  </div>
                </div>
              </drop-down-menu-item>
              <drop-down-menu-item class="block md:hidden"
                v-if="video.exportStatus !== 'EXPORTING' && !isLive && video.downloadUrl"
                @click="downloadDvrArchive()">
                <icon-base class="mr-2" fill="none">
                  <icon-download />
                </icon-base>
                Download
              </drop-down-menu-item>
              <drop-down-menu-item class="block md:hidden" v-if="video.exportStatus === 'EXPORTED'"
                @click="toggleMetadata(video)">
                <icon-base class="mr-2" fill="none">
                  <icon-link />
                </icon-base>
                Embed link
              </drop-down-menu-item>
              <drop-down-menu-item class="block md:hidden" v-if="video.exportStatus === 'NOT_EXPORTED'"
                @click="playPreview('Audio track 1', false, 'preview-video')">
                Preview
              </drop-down-menu-item>
              <drop-down-menu-item class="block md:hidden" v-if="video.exportStatus === 'NOT_EXPORTED'"
                @click="onVodSaveWhole">
                Save whole video
              </drop-down-menu-item>
              <drop-down-menu-item v-if="video.exportStatus === 'NOT_EXPORTED'"
                @click="onVodSaveCustom('Audio track 1')">
                Select a range
              </drop-down-menu-item>
              <drop-down-menu-item @click="requestDeleteDvrArchive()">
                <icon-base class="mr-2" fill="none">
                  <icon-bin />
                </icon-base>
                Delete
              </drop-down-menu-item>
            </template>
          </drop-down-menu>
        </div>
      </div>
    </div>
    <div :ref="'metaDataCollapse' + video.from + video.duration + video.episodeId" class="accordion-content"
      style="display: none;">
      <div class="accordion-item">
        <div class="accordion-body">
          <TabGroup type="segmented" class="my-6">
            <TabItem
              :is-active="embedContentActiveTab === 'full'"
              @click="switchEmbedContentTab('full')"
            >
              Video + Audio
            </TabItem>
            <TabItem
              :is-active="embedContentActiveTab === 'audio'"
              @click="switchEmbedContentTab('audio')"
            >
              Audio only
            </TabItem>
          </TabGroup>
          <div class="field-container">
            <ButtonCopyToClipboard class="absolute z-10 right-7 top-7" :text="getPlayerUrl(video)" />
            <a target="_blank"
              class="absolute right-2 z-10 top-7 w-7 h-7 flex items-center justify-center text-surface-8 hover:text-white"
              :href="getPlayerUrl(video)">
              <icon-base fill="none">
                <icon-link-external />
              </icon-base>
            </a>
            
            <FormText label="Embed Url" readonly noresize fit-content custom-classes="py-3 mb-4"
              @click="clipboardCopy(getPlayerUrl(video))" :model-value="getPlayerUrl(video)" />
          </div>
          <div class="field-container">
            <ButtonCopyToClipboard class="absolute z-10 right-2 top-7" :text="getEpisodeIframeSnippet(video)" />
            <FormText noresize label="Iframe Snippet" readonly fit-content custom-classes="pr-5 py-3 mb-4"
              @click="clipboardCopy(getEpisodeIframeSnippet(video))" :model-value="getEpisodeIframeSnippet(video)" />
          </div>
          <div v-if="hasHlsLink" class="field-container mt-3">
            <ButtonCopyToClipboard class="absolute z-10 right-2 top-7" :text="getHlsLink()" />
            <FormText noresize label="HLS Manifest" readonly fit-content custom-classes="pr-5 py-3 mb-4"
              @click="clipboardCopy(getHlsLink())" :model-value="getHlsLink()" />
          </div>
        </div>
      </div>
    </div>
    <add-pre-record-stream-modal v-if="selectedVideo" :video="this.selectedVideo" :stream="this.stream"
      @modal-dismiss="onCreatePrerecordDismiss" @modal-confirm="onCreatePrerecordConfirm" />
  </div>
</template>

<script>
import { showModal, hideModal } from '@/Utils/modalBus'
import _ from 'lodash';
import StreamService from "@/services/StreamService";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import AddPreRecordStreamModal from "@/components/modals/AddPreRecordStreamModal.vue";
import UserService from "@/services/UserService";
import ClipLoader from "@/components/ui/ClipLoader.vue";
import IconBase from '@/components/icon/IconBase.vue'
import IconBucket from '@/components/icon/IconBucket.vue'
import prettyMilliseconds from "pretty-ms";
import { mapGetters } from 'vuex'
import { date } from '@/filtersNew'
import IconPlay from '../icon/IconPlay.vue';
import IconDownload from '../icon/IconDownload.vue';
import IconCaptions from '../icon/IconCaptions.vue';
import FormButton from '../Atoms/FormButton.vue';
import FormText from '../Atoms/FormText.vue';
import TabGroup from '../Atoms/TabGroup.vue';
import TabItem from '../Atoms/TabItem.vue';
import IconGear from '../icon/IconGear.vue';
import IconBin from '../icon/IconBin.vue';
import IconEdit from '../icon/IconEdit.vue';
import IconCopy from '../icon/IconCopy.vue';
import IconLinkExternal from '../icon/IconLinkExternal.vue';
import IconLink from '../icon/IconLink.vue';
import Tooltip from '../Atoms/Tooltip.vue';
import Badge from '../Badge.vue';
import Title from './Title.vue';
import IconElipsis from '../icon/IconElipsis.vue';
import IconCameraPlus from '../icon/IconCameraPlus.vue';
import IconCameraCinema from '../icon/IconCameraCinema.vue';
import IconElipsisVertical from '../icon/IconElipsisVertical.vue';
import DropDownMenu from '../Atoms/DropDownMenu.vue';
import DropDownMenuItem from '../Atoms/DropDownMenuItem.vue';
import IconInfo from '../icon/IconInfo.vue';
import IconChevronRight from '@/components/icon/IconChevronRight.vue';
import IconSelectRange from '@/components/icon/IconSelectRange.vue';
import ButtonCopyToClipboard from '@/components/Moleculas/ButtonCopyToClipboard.vue';
import { audioTrackName, loadDvrAudioTracks } from '@/Utils/dvrAudioTracks';


export default {
  name: "DVRVideoListItem",
  components: {
    AddPreRecordStreamModal,
    ConfirmModal,
    ClipLoader,
    IconBase,
    IconBucket,
    IconPlay,
    IconDownload,
    IconCaptions,
    IconGear,
    IconBin,
    IconEdit,
    IconInfo,
    IconCameraPlus,
    IconCopy,
    IconElipsis,
    IconElipsisVertical,
    IconLinkExternal,
    IconLink,
    FormButton,
    FormText,
    TabGroup,
    TabItem,
    DropDownMenu,
    DropDownMenuItem,
    Tooltip,
    Badge,
    Title,
    IconCameraCinema,
    IconChevronRight,
    IconSelectRange,
    ButtonCopyToClipboard
  },
  props: {
    video: {
      type: Object,
      required: true,
    },
    stream: {
      type: Object,
      required: true,
    },
    vodStorageExceeded: {
      type: Boolean,
      default: false,
    },
    isStreamLive: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedVideo: null,
      archiveName: "",
      lockProcessing: false,
      statusTimerId: null,
      isLive: this.video.live,
      saveVodOptShowed: false,
      embedContentActiveTab: "full",
      expiredSub: false,
      allowOverdueVodDownload: true,
      streamDvrEnabled: null,
      audioTracks: null,
      disabledTooltipMessage: 'Upgrade to use the download feature. Check details on <a class="text-reset" target="_blank" href="https://castr.com/pricing/">Pricing page</a>'
    };
  },
  computed: {
    activeTeam() {
      return this.$store.state.User.activatedTeam;
    },
    hasHlsLink() {
      //allow for singlemusic
      const userList = ["5eb70a3188bbc76c25c73991", "5b2e1c65f1e043163435d4e9"];
      return userList.includes(UserService.getUserId(this.activeTeam));
    },
    planUnlimVodDownload() {
      const sub = this.$store.state.User.addonSub.find(({ category }) => category === 'live')
      if (!sub) {
        return false
      }

      return _.get(sub, ['package', 'definition', 'unlimitedVodDownload'], false)
    },
    ...mapGetters({
      scheduledStreams: 'Streams/scheduledStreams',
      maxScheduledStreams: 'User/maxScheduledStreams',
      isOwner: 'User/isOwner',
      parentAccount: 'User/parentAccount',
      userPermissions: 'User/userPermissions',
      teamOwner: 'User/teamOwner'
    }),
    disableCreatePreRecorderedStream() {
      if (this.userPermissions && !this.userPermissions.preRecordedStreamManage && !this.isOwner) {
        return true
      } else return false
    },
    audioTracksLoading() {
      return this.audioTracks === null
    },
    audioTrackNames() {
      return (this.audioTracks || []).map(audioTrackName)
    },
    isPreRecordedLimitReached() {
      return this.scheduledStreams.length >= this.maxScheduledStreams
    },
    preRecordedUsageTooltip() {
      if (this.isPreRecordedLimitReached) {
        return `You have used ${this.scheduledStreams.length}/${this.maxScheduledStreams} pre-recorded streams. Need more? <a href="/subscribe?category=live" target="_blank" class="underline hover:text-white">Upgrade</a> here`
      }
      return 'Number of pre-recorded streams used'
    },
    vodStorageExceededTooltip() {
      return 'Saving as VOD is disabled because your storage limit is exceeded. Upgrade your plan for more storage.';
    },
    tooltipText() {
      return `
				<span class="text-dark-8 text-small">
					You don’t have permission. Contact the team owner at <a class="text-dark-8 text-small"
					:href="'mailto:${this.userEmail}'">${this.userEmail}</a> to gain access.
				</span>`
    }
  },
  watch: {
    "video.live": function (newVal, oldVal) {
      this.isLive = newVal;
    },
  },
  async mounted() {
    this.archiveName = this.video.name ? this.video.name : this.video.fileName;
    const user = UserService.getUser(this.activeTeam)
    let unlimVodDownload = false
    this.streamDvrEnabled = this.stream.metadata.dvrArchive;

    // allow if plan has feature or user account allowed
    if (this.planUnlimVodDownload || _.isBoolean(user.unlimitedVodDownload) && user.unlimitedVodDownload) {
      unlimVodDownload = true
    }

    // explicitly block for user account
    if (_.isBoolean(user.unlimitedVodDownload) && !user.unlimitedVodDownload) {
      unlimVodDownload = false
    }

    if (Math.round(new Date().getTime() / 1000) - this.video.from > 86400 * 3 && !unlimVodDownload && this.video.exportStatus === 'EXPORTED') {
      // this.video.downloadUrl = null;
      this.allowOverdueVodDownload = false;
    }

    const subscriptions = this.$store.state.User.subscriptions;
    const subDetail = _.filter(subscriptions, (sub) => sub.category === this.stream.type && sub.enabled === true)
    if (subDetail.length === 0 || subDetail[0].enabled === false) {
      this.expiredSub = true;
      this.disabledTooltipMessage = 'Please renew your subscription plan to use this feature.'
    }
  },
  methods: {
    switchEmbedContentTab(tab) {
      this.embedContentActiveTab = tab;
    },
    toggleEmbedLink() {
      this.video.expanded = !this.video.expanded;
    },
    downloadDvrArchive() {
      this.video.downloadUrl = this.video.downloadUrl.replace(
        ".castr.io",
        ".global.ssl.fastly.net"
      );
      window.location.href = this.video.downloadUrl + '?packing=compat';
    },
    playPreview(audioTrack, audioOnly, action = 'video') {
      if (typeof audioTrack === 'string') {
        this.video.audioTrack = { name: audioTrack, audioOnly: audioOnly }
      } else delete this.video.audioTrack
      this.$emit("reqeust-video-preview", { video: this.video, action });
    },
    requestDeleteDvrArchive() {
      this.$emit("request-dvr-archive-delete", this.video);
    },
    async onNameChange(newName) {
      if (this.video.fileName === newName) {
        return;
      }

      try {
        const q = {
          streamId: this.stream._id,
          from: this.video.from,
          duration: this.video.duration,
          name: newName,
        };

        await StreamService.setStreamDvrArchiveName(q);
        this.video.name = newName;
        this.$notify({
          group: "success",
          // title: "Couldn't change archive name",
          text: `The ${newName} name has been updated`,
        });
      } catch (err) {
        this.$notify({
          group: "error",
          title: "Couldn't change archive name",
          text: err.message,
        });
      }
    },
    exportDvrArchive2() {
      this.video.again = true;
      this.$emit("request-dvr-archive-export", this.video);
    },
    exportDvrArchive() {
      this.$emit("request-dvr-archive-export", this.video);
    },
    clipboardCopy(text) {
      try {
        if (text instanceof Function) text = text();

        this.$copyText(text);
        this.$notify({ group: "info", text: "Copied to clipboard" });
      } catch (e) { }
    },
    getPlayerUrl() {
      if (this.stream.type === "live") {
        const abr = this.video.abr ? "true" : "false";
        const audioOnly = this.embedContentActiveTab === "audio" ? true : false;
        return (
          `${process.env.VUE_APP_PLAYER_APP_BASE_URL}/${this.stream.key}?range=${this.video.from}-${this.video.duration}&abr=${abr}`
          + (this.video.namedHls ? '&namedHls=true' : '')
          + (this.video.cap ? '&cap=true' : '')
          + (audioOnly ? "&onlyAudio=true" : "")
        );
      }
    },
    getHlsLink() {
      const host = "singlemusic-archives.akamaized.net";
      const url = `https://${host}/episodes/edge-en/${this.stream.key}/archive-${this.video.from}-${this.video.duration}.mp4/index.m3u8`;
      return url;
    },
    getEpisodeIframeSnippet() {
      const iframeSrc = this.getPlayerUrl(this.video);
      return `<iframe src="${iframeSrc}" frameborder="0" width="590" height="430" allow="autoplay; encrypted-media"  scrolling="no"  allowfullscreen webkitallowfullscreen mozallowfullscreen oallowfullscreen msallowfullscreen></iframe>`;
    },
    showVodSaveOpt() {
      this.saveVodOptShowed = !this.saveVodOptShowed;
    },
    onVodSaveWhole() {
      this.saveVodOptShowed = false;
      this.exportDvrArchive();
    },
    onVodSaveWithCaptions() {
      this.saveVodOptShowed = false;
      this.video.packingCompat = false
      this.exportDvrArchive();
    },
    onVodSaveCustom(audioTrack, action = this.stream.type === 'live' ? 'save-as-vod-video' : 'save-multistream') {
      this.saveVodOptShowed = false;
      this.playPreview(audioTrack, false, action);
    },
    onVodAudioSaveCustom(audioTrack, action = 'download-audio') {
      this.saveVodOptShowed = false;
      this.playPreview(audioTrack, true, action);
    },
    async onDownloadMenuToggle(visible) {
      if (!visible || this.audioTracks) return;
      this.audioTracks = await loadDvrAudioTracks(this.video, this.stream);
    },
    onCreatePrerecord(video) {
      this.selectedVideo = video;
      setTimeout(() => {
        showModal('modal-add-pre-record' + video.name);
      }, 100);

    },
    onCreatePrerecordDismiss() {
      this.selectedVideo = null
    },
    onCreatePrerecordConfirm() {
      this.selectedVideo = null
    },
    humanTime(t) {
      return prettyMilliseconds(t * 1000, { unitCount: 2 });
    },
    dateFormated(item) {
      return date(item, 'hh:mm A DD/MM/YY')
    },
    dateOnly(item) {
      return date(item, 'MMM D, YYYY')
    },
    timeOnly(item) {
      return date(item, 'h:mm A')
    },
    toggleMetadata(video) {
      const refName = 'metaDataCollapse' + video.from + video.duration + video.episodeId;
      const element = this.$refs[refName];
      if (element) {
        element.style.display = element.style.display === 'none' ? 'block' : 'none';
      }
    },
  },
  beforeUnmount() {
    clearInterval(this.video.exportStatusTimer);
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.video-item {
  font-size: var(--font-s-s);
}

.field-container {
  position: relative;
}

.btn-icon_a {
  position: absolute;
  right: 0;
  top: 30px;
}

.btn-icon_b {
  position: absolute;
  right: 25px;
  top: 30px;
}

.video-item .subtitle_input {
  font-weight: 400;
  font-size: var(--font-s-m);
  color: var(--c-dark-9);
}

.textarea_snippet,
.textarea_url {
  resize: none;
}

@media (max-width: 767px) {
  .video-item>.row {
    margin-right: -5px;
    margin-left: -5px;
  }

  :deep(.video-item .row>div) {
    padding-right: 5px;
    padding-left: 5px;
  }

  .spinner-mobile {
    position: absolute;
    top: -5px;
    left: -12px;
  }
}
</style>
